// Content merge utilities for admin-editable public-page copy.
//
// The in-code `*_COPY` defaults are the source of truth for shape. The backend
// stores only *overrides* (serializable fields an admin edited). We deep-merge
// overrides over the defaults so that:
//   1. The page renders even before the DB table exists or any admin save.
//   2. Non-serializable defaults (e.g. JSX article bodies in /help) are preserved
//      because the stored overrides never include them.

export type Json =
  | string
  | number
  | boolean
  | null
  | Json[]
  | { [key: string]: Json };

export function isSerializableLeaf(value: unknown): boolean {
  return (
    value === null ||
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

// Convert a (possibly JSX-containing) value into a fully JSON-serializable
// structure, omitting anything that can't be serialized (functions, React
// elements, symbols, undefined). Used to build the payload an admin saves.
export function toSerializable(value: unknown): unknown {
  if (isSerializableLeaf(value)) return value;
  if (Array.isArray(value)) {
    const out: unknown[] = [];
    for (const item of value) {
      const s = toSerializable(item);
      if (s !== undefined) out.push(s);
    }
    return out;
  }
  if (typeof value === "object" && value !== null) {
    const out: Record<string, unknown> = {};
    for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
      const s = toSerializable(v);
      if (s !== undefined) out[key] = s;
    }
    return out;
  }
  // functions, JSX, symbols, etc. -> omit
  return undefined;
}

// Deep-merge an override over a base. Objects merge by key; arrays merge by
// index (so removing items from a saved override does not resurrect deleted
// defaults, and adding is simply ignored). Serializable leaves take the override.
export function deepMerge<T>(base: T, override: unknown): T {
  if (override === undefined || override === null) return base;
  if (isSerializableLeaf(override)) return override as T;

  if (Array.isArray(base) && Array.isArray(override)) {
    return base.map((item, i) => deepMerge(item, override[i])) as unknown as T;
  }

  if (
    typeof base === "object" &&
    base !== null &&
    typeof override === "object" &&
    override !== null &&
    !Array.isArray(base) &&
    !Array.isArray(override)
  ) {
    const out: Record<string, unknown> = {
      ...(base as Record<string, unknown>),
    };
    for (const [key, v] of Object.entries(
      override as Record<string, unknown>,
    )) {
      out[key] = deepMerge((base as Record<string, unknown>)[key], v);
    }
    return out as T;
  }

  return override as T;
}
